/**
 * Release Notes Page (Issue #933)
 *
 * Shows release history with version summaries.
 * Uses data-driven approach with CMS foundation for future expansion.
 *
 * Architecture:
 * - useReleaseNotes hook (static data now, API-ready for Phase 2)
 * - ContentSectionRenderer for modular content blocks
 * - Layer 2 CSS tokens per ORO design system
 */

import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import Divider from '../components/Divider';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useReleaseNotes } from '../hooks/useReleaseNotes';
import { ContentSectionRenderer } from '../components/cms/ContentSectionRenderer';

export default function ReleaseNotesPage() {
  const navigate = useNavigate();
  const { data: releaseNotes } = useReleaseNotes();

  useDocumentTitle('Release Notes');

  return (
    <PageLayout title="Release Notes">
      <PageHeader
        title="Release Notes"
        subtitle="What's new in ORO move"
      />

      {releaseNotes.map((release, index) => (
        <div key={release.version}>
          {index > 0 && <Divider />}

          <Section showDivider={false}>
            {/* Version header */}
            <div className="flex items-center gap-3 mb-4">
              {release.isCurrent && (
                <span className="bg-accent text-black px-3 py-1 rounded-full text-sm font-semibold">
                  Current
                </span>
              )}
              {release.codename && (
                <span className="bg-oro-surface-alt border border-oro-border px-3 py-1 rounded-full text-sm font-medium text-text-secondary">
                  {release.codename}
                </span>
              )}
              <h2 className="text-xl font-bold text-text-primary">{release.version}</h2>
              <span className="text-text-muted text-sm">{release.releaseDate}</span>
            </div>

            {/* Summary */}
            {release.summary && (
              <p className="text-text-secondary leading-relaxed mb-4">{release.summary}</p>
            )}

            {/* Sections */}
            <div className="space-y-6">
              {release.sections.map((section) => (
                <ContentSectionRenderer key={section.id} section={section} />
              ))}
            </div>
          </Section>
        </div>
      ))}

      <Divider />

      <Section showDivider={false}>
        <div className="text-center space-y-4">
          <p className="text-text-muted">
            Have feedback on a feature?
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="text-accent hover:text-accent-light transition-colors"
          >
            Let us know what you think
          </button>
        </div>
      </Section>
    </PageLayout>
  );
}
